import { useEffect, useState } from "react";
import { Crown, Sparkles, Anchor, Compass, ScrollText, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import PartnerInquiry from "@/components/PartnerInquiry";
import ScoutReport from "@/components/ScoutReport";

const PILLAR_ICON = {
    fleet: Anchor,
    ghost: Sparkles,
    scout: Compass,
    legacy: Crown,
};

export default function Mission() {
    const [mission, setMission] = useState(null);

    useEffect(() => {
        api.get("/hub/mission")
            .then(({ data }) => setMission(data))
            .catch(() => setMission(null));
    }, []);

    if (!mission) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-brown text-sm tracking-[0.3em] uppercase">
                Crafting your experience…
            </div>
        );
    }

    const taxPct = `${Math.round((mission.legacy_tax?.rate ?? 0.1) * 100)}%`;

    return (
        <div className="bg-cream" data-testid="mission-page">
            {/* Sovereign Hero — Mustard Gold over Forest */}
            <section className="relative bg-forest text-cream overflow-hidden">
                <div className="grain absolute inset-0 opacity-50" />
                <div
                    className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full blur-3xl opacity-25"
                    style={{ background: "var(--ig-gold)" }}
                    aria-hidden
                />
                <div
                    className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
                    style={{ background: "var(--ig-olive)" }}
                    aria-hidden
                />
                <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-20 pb-24 md:pt-32 md:pb-36">
                    <div className="max-w-4xl rise-in">
                        <span className="divider-rule text-gold">
                            <ScrollText size={12} /> EIGOO Mission Manifest
                        </span>
                        <h1 className="font-serif text-6xl sm:text-7xl md:text-8xl lg:text-[7rem] leading-[0.95] mt-8">
                            <span className="block text-cream">Emperor</span>
                            <span className="block italic text-cream/85">
                                of the
                            </span>
                            <span
                                className="block"
                                style={{ color: "var(--ig-gold)" }}
                            >
                                Future.
                                <Sparkles
                                    size={36}
                                    className="inline-block ml-4 floaty"
                                    aria-hidden
                                />
                            </span>
                        </h1>
                        <p className="mt-10 text-lg md:text-xl text-cream/75 leading-relaxed max-w-2xl">
                            {mission.subhead}
                        </p>
                    </div>
                </div>
            </section>

            {/* Manifest pillars (4 lines) */}
            <section
                className="bg-cream py-20 md:py-28"
                data-testid="mission-manifest"
            >
                <div className="max-w-5xl mx-auto px-5 md:px-10">
                    <span className="divider-rule text-brown">
                        Four Lines · One Hub
                    </span>
                    <ol className="mt-10 space-y-8">
                        {mission.manifest.map((line, i) => (
                            <li
                                key={i}
                                className="grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-baseline rise-in"
                                style={{ animationDelay: `${i * 90}ms` }}
                            >
                                <span
                                    className="font-serif text-5xl md:text-6xl"
                                    style={{ color: "var(--ig-gold)" }}
                                >
                                    0{i + 1}
                                </span>
                                <p className="font-serif text-2xl md:text-3xl text-forest leading-snug">
                                    {line}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* Legacy Tax — high-fidelity gold treatment */}
            <section
                className="relative py-24 md:py-32 overflow-hidden"
                style={{
                    background:
                        "radial-gradient(ellipse at center, var(--ig-gold-soft) 0%, var(--ig-gold) 35%, #b8862f 100%)",
                }}
                data-testid="mission-legacy-tax"
            >
                <div className="grain absolute inset-0 opacity-50" />
                <div
                    className="absolute inset-0 opacity-20"
                    style={{
                        backgroundImage:
                            "url('https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&w=2000&q=80')",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        mixBlendMode: "multiply",
                    }}
                    aria-hidden
                />
                <div className="relative max-w-5xl mx-auto px-5 md:px-10 text-center">
                    <span
                        className="divider-rule"
                        style={{ color: "var(--ig-forest)" }}
                    >
                        <Crown size={12} /> Round-Up · CyberKids Give-Back
                    </span>
                    <p
                        className="font-serif italic text-2xl md:text-3xl mt-8"
                        style={{ color: "rgba(26,43,33,0.85)" }}
                    >
                        Of every opted-in checkout
                    </p>
                    <h2
                        className="font-serif text-[7rem] sm:text-[10rem] md:text-[14rem] leading-[0.85] mt-2"
                        style={{ color: "var(--ig-forest)" }}
                    >
                        {taxPct}
                    </h2>
                    <p
                        className="font-serif text-3xl md:text-5xl mt-2"
                        style={{ color: "var(--ig-forest)" }}
                    >
                        funds the{" "}
                        <span className="italic">
                            {mission.legacy_tax?.beneficiary}.
                        </span>
                    </p>
                    <p
                        className="mt-8 max-w-xl mx-auto text-base md:text-lg leading-relaxed"
                        style={{ color: "rgba(26,43,33,0.78)" }}
                    >
                        {mission.legacy_tax?.purpose} A small switch at
                        checkout. A real horizon for an island generation.
                    </p>
                </div>
            </section>

            {/* Pillars grid */}
            <section
                className="bg-cream-warm py-20 md:py-24"
                data-testid="mission-pillars"
            >
                <div className="max-w-7xl mx-auto px-5 md:px-10">
                    <span className="divider-rule text-brown">The Four Pillars</span>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {mission.pillars.map((p, i) => {
                            const Icon = PILLAR_ICON[p.key] || Sparkles;
                            return (
                                <article
                                    key={p.key}
                                    className="p-6 bg-cream rounded-sm border border-forest/10 hover:border-gold transition-colors rise-in"
                                    style={{ animationDelay: `${i * 100}ms` }}
                                >
                                    <Icon
                                        size={20}
                                        className="text-gold"
                                    />
                                    <p className="text-[0.6rem] tracking-[0.3em] uppercase text-brown mt-4">
                                        0{i + 1}
                                    </p>
                                    <h3 className="font-serif text-2xl text-forest mt-1">
                                        {p.label}
                                    </h3>
                                    <p className="text-sm text-forest/80 mt-3 leading-relaxed">
                                        {p.body}
                                    </p>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Partner Inquiry */}
            <PartnerInquiry />

            {/* Closing CTA */}
            <section className="bg-forest text-cream py-20 text-center relative overflow-hidden">
                <div className="grain absolute inset-0" />
                <div className="relative max-w-3xl mx-auto px-6">
                    <h3 className="font-serif text-4xl md:text-5xl">
                        Share the island.
                    </h3>
                    <p className="mt-5 text-cream/70 leading-relaxed">
                        Independent brands. Natural ingredients. Ten percent
                        rounded up for the next generation. Welcome to Island Gold.
                    </p>
                    <a
                        href="#partner-inquiry"
                        className="btn-pill btn-gold mt-10 inline-flex"
                    >
                        Send a Partner Inquiry <ArrowRight size={14} />
                    </a>
                </div>
            </section>
        </div>
    );
}
